/**
 * The error collector behind `__marg.errors()` (architecture §8.1): console errors, uncaught errors, unhandled
 * rejections, WebGPU uncaptured errors and app-reported failures (device lost, failed screens). Installed at boot step 1
 * so failures from step 2 on are captured; captures fail when the list is non-empty.
 */
export class ErrorLog {
  private readonly list: string[] = [];
  private installed = false;
  /** Listener for new entries (diagnostics, the perf overlay counter). */
  onRecord: ((message: string) => void) | null = null;

  record(message: string): void {
    this.list.push(message);
    if (this.list.length > 500) this.list.shift();
    try { this.onRecord?.(message); } catch { /* never throw from the collector */ }
  }

  errors(): string[] { return this.list.slice(); }
  get count(): number { return this.list.length; }

  /** Hooks window.onerror, unhandledrejection and console.error. Idempotent. */
  install(win: Window): void {
    if (this.installed) return;
    this.installed = true;
    win.addEventListener('error', (e) => {
      const target = e.target as Element | null;
      if (target && target !== (win as unknown) && target instanceof Element) {
        this.record(`resource failed: <${target.tagName.toLowerCase()}> ${(target as HTMLImageElement).src ?? (target as HTMLLinkElement).href ?? ''}`);
        return;
      }
      this.record(`uncaught: ${e.message}${e.filename ? ` (${e.filename.split('/').pop()}:${e.lineno})` : ''}`);
    }, true);
    win.addEventListener('unhandledrejection', (e) => {
      const r = e.reason as { message?: string } | undefined;
      this.record(`unhandled rejection: ${r?.message ?? String(e.reason)}`);
    });
    const orig = console.error.bind(console);
    console.error = (...args: unknown[]): void => {
      this.record(`console.error: ${args.map(fmt).join(' ')}`);
      orig(...args);
    };
  }
}

function fmt(x: unknown): string {
  if (x instanceof Error) return `${x.name}: ${x.message}`;
  if (typeof x === 'string') return x;
  try { return JSON.stringify(x); } catch { return String(x); }
}
